import type { GenericObjectType } from '@rjsf/utils';
import { get, isEmpty } from 'es-toolkit/compat';
import { findBestFlavorForTarget } from 'features/traffic/campaigns/TPCampaignOverview/overview-items/TPCampaignOverviewFlavorSelector/flavor-cloning-utils';
import type { TpCampaignRequiredFormFlavorFragment } from 'generated/graphql';
import { trimTrailingArrayIndex } from './array-path';
import { flavorsMatch, getTargetActiveFlavorIndexes, type HasSchedulingInfo } from './flavor-filtering';
import { getSharingConfig } from './schema-config';
import { type DMCCOverrideMap, type EffectiveSharingMode, SHARING_MODE, type SharingConfig, type SharingMode } from './types';

/**
 * Evaluates the effective sharing mode for a field using a 3-tier priority system.
 *
 * ## Priority Algorithm:
 * 1. **Tier 1 (User Override)**: DMCC `dmccOverrideMap` user preferences - highest priority when available
 * 2. **Tier 2 (Default)**: UI schema `defaultSharingMode` - schema-defined default behavior
 * 3. **Tier 3 (Forced)**: UI schema `forcedSharingMode` - final fallback when no override/default
 *
 * Business Logic Notes:
 * - Override and default tiers only apply when dmccOverrideMap exists (feature flag)
 * - 'structure' mode is transformed to 'all' for content sharing compatibility
 * - When dmccOverrideMap is undefined, only forced sharing mode is evaluated
 * - Returns undefined when no sharing mode determined - caller handles priority-based fallback
 *
 * @param params.fieldPathArray - Array path segments to look up in dmccOverrideMap
 * @param params.dmccOverrideMap - When undefined, acts as feature flag to disable override/default system
 * @returns undefined when no sharing mode determined - caller handles priority-based fallback
 */
export function evaluateEffectiveSharingMode({
  fieldPathArray,
  sharingConfig,
  dmccOverrideMap,
}: {
  fieldPathArray: PropertyKey[];
  sharingConfig?: SharingConfig;
  dmccOverrideMap?: DMCCOverrideMap;
}): EffectiveSharingMode | undefined {
  // Feature flag check - only apply override and default when dmccOverrideMap exists
  if (dmccOverrideMap) {
    // Tier 1: User Override (DMCC user preferences - highest priority
    const userOverride = get(dmccOverrideMap, trimTrailingArrayIndex(fieldPathArray));
    if (userOverride && typeof userOverride === 'string') return userOverride as EffectiveSharingMode;

    // Tier 2: Default Sharing Mode (schema-defined default behavior)
    if (sharingConfig?.defaultSharingMode) return sharingConfig.defaultSharingMode;
  }

  // Tier 3: Forced Sharing (final fallback)
  if (sharingConfig?.forcedSharingMode) {
    // Special case: 'structure' mode becomes 'all' for content sharing
    return sharingConfig.forcedSharingMode === SHARING_MODE.STRUCTURE ? SHARING_MODE.ALL : sharingConfig.forcedSharingMode;
  }

  // No sharing mode
}

/**
 * Complete BRD evaluation that returns target flavor indexes for propagation.
 *
 * Combines effective sharing mode evaluation with target flavor filtering in a single operation.
 * This is the primary entry point for determining which flavors should receive field updates
 * when a user edits content in the campaign builder.
 *
 * @param params.fieldPathArray - Array path segments for looking up sharing config
 * @param params.dmccOverrideMap - Feature flag and user preference overrides for sharing behavior
 * @returns Empty array when no propagation should occur (no sharing mode or no matching flavors)
 */
export function evaluateFieldSharingPropagation({
  fieldPathArray,
  uiSchema,
  allFlavors,
  currentFlavor,
  currentFlavorIndex,
  dmccOverrideMap,
}: {
  fieldPathArray: PropertyKey[];
  uiSchema: GenericObjectType;
  allFlavors: TpCampaignRequiredFormFlavorFragment[];
  currentFlavor: TpCampaignRequiredFormFlavorFragment;
  currentFlavorIndex: number;
  dmccOverrideMap?: DMCCOverrideMap;
}): number[] {
  const sharingConfig = getSharingConfig(fieldPathArray, uiSchema);
  const sharingMode = evaluateEffectiveSharingMode({
    dmccOverrideMap,
    fieldPathArray,
    sharingConfig,
  });

  if (!sharingMode) return [];

  // Handle single sharing mode (simplified - no more Tier 3 arrays)
  return getTargetActiveFlavorIndexes({
    allFlavors,
    currentFlavor,
    currentFlavorIndex,
    sharingMode,
  });
}

/**
 * Extracts field values from matching flavors using sharing mode evaluation.
 *
 * Combines sharing mode evaluation with flavor matching to extract field values for content
 * cloning scenarios. Uses the 3-tier sharing system from `evaluateEffectiveSharingMode` and
 * falls back to priority-based flavor selection when no sharing mode is configured.
 *
 * Critical for maintaining content consistency when creating new flavors - ensures users
 * see appropriate pre-filled values based on sharing configuration rather than empty forms.
 *
 * @param params.fieldPathArray - Array of path segments to the field (e.g., ['arguments', 'messaging', 'title'])
 * @param params.dmccOverrideMap - Feature flag and user preference overrides for sharing behavior
 * @returns Field value from best matching flavor, or undefined if no suitable source found
 */
export function evaluateFieldValueWithBRD({
  fieldPathArray,
  uiSchema,
  activeFlavors,
  schedulingInfo,
  dmccOverrideMap,
}: {
  fieldPathArray: PropertyKey[];
  uiSchema: GenericObjectType;
  activeFlavors: TpCampaignRequiredFormFlavorFragment[];
  schedulingInfo: HasSchedulingInfo;
  dmccOverrideMap?: DMCCOverrideMap;
}): unknown {
  // Validate essential parameters
  if (isEmpty(fieldPathArray) || !Array.isArray(activeFlavors)) return;

  // biome-ignore lint/nursery/noUnnecessaryConditions: legacy code
  const sharingConfig = getSharingConfig(fieldPathArray, uiSchema || {});

  // Helper function to apply tier sharing logic and extract field values
  const applyTierSharingLogic = (sharingModes: SharingMode[]): unknown => {
    // Find flavors that match the specified sharing mode(s)
    // Uses centralized flavor matching logic for consistency across the codebase
    const matchingFlavors = activeFlavors.filter((flavor1) =>
      sharingModes.some((sharingMode) =>
        flavorsMatch({
          flavor1,
          flavor2: schedulingInfo,
          sharingMode,
        }),
      ),
    );

    if (isEmpty(matchingFlavors)) return;

    // Use the provided findBestFlavorForTarget function to select the best match
    const bestFlavor = findBestFlavorForTarget(schedulingInfo, matchingFlavors);
    if (!bestFlavor) return;

    return get(bestFlavor.content, fieldPathArray);
  };

  // Progressive BRD tier degradation - try each tier until we find matches
  const tierConfigurations = [
    // Tier 1: Full BRD evaluation (User Override > Default > Forced)
    {
      dmccOverrideMap,
      sharingConfig,
    },

    // Tier 2: Skip user overrides (Default > Forced)
    {
      dmccOverrideMap: undefined,
      sharingConfig,
    },

    // Tier 3: Skip user overrides and defaults (Forced only)
    {
      dmccOverrideMap: undefined,
      ...(sharingConfig && {
        ...sharingConfig,
        defaultSharingMode: undefined,
      }),
    },
  ];

  // Try each BRD tier until we find a match
  for (const tierConfig of tierConfigurations) {
    const effectiveSharingMode = evaluateEffectiveSharingMode({
      fieldPathArray,
      ...tierConfig,
    });

    if (effectiveSharingMode) {
      const result = applyTierSharingLogic([
        effectiveSharingMode,
      ]);
      if (result !== undefined) return result;
    }
  }

  // Priority Fallback - All BRD tiers failed to find matches
  // Use priority-based flavor selection (locale → language+platform → language → marketplace → platform → first created)
  const bestFlavor = findBestFlavorForTarget(schedulingInfo, activeFlavors);
  if (bestFlavor) return get(bestFlavor.content, fieldPathArray);
}
